import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  MultiMediaResponse,
} from 'dtos';
import {
  useNotify,
} from 'core/hooks';
import IconButton from 'core/components/IconButton';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import Spinner from 'pages/Dashboard/components/Spinner';
import usePreviewMediaClick from 'pages/Dashboard/hooks/usePreviewMediaClick';
import FaxIcon from 'core/icons/fax';
import PrinterIcon from 'core/icons/printer';
import DownloadIcon from 'core/icons/download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  getApiMultimediaGetPatientMultiMediaByIdPatientMultiMediaId,
} from 'endpoints';
import {
  faxEventChannel,
  VisitNoteFormPreviewDialogParams,
} from 'pages/Dashboard/services/eventChannels';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  downloadFileFromUrl,
} from 'pages/Dashboard/utils/helper';
import {
  usePrintMediaRequest,
  useResolvePatientMultimedia,
} from 'views/EMR/hooks/media';
import {
  isOrderOrReferralCategory,
} from 'views/EMR/components/FormWorkflow/utils';
import {
  useDefaultPatient,
} from 'views/EMR/stores/entities';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';

type Props = VisitNoteFormPreviewDialogParams & {
  onClose: () => void;
};

type DocumentRowProps = {
  media: MultiMediaResponse;
  onPrint: (mediaId: number) => void;
  onPreview: (media: MultiMediaResponse) => void;
  printingMediaId: number | null;
  isPrinting: boolean;
};

function FormWorkflowDocumentRow({
  media,
  onPrint,
  onPreview,
  printingMediaId,
  isPrinting,
}: DocumentRowProps) {
  const notify = useNotify();
  const [isDownloading, setIsDownloading] = useState(false);
  const mediaId = media.mediaId ?? 0;
  const downloadUrl = media.url?.value ?? '';
  const isRowPrinting = isPrinting && printingMediaId === mediaId;

  const handleDownload = useCallback(async () => {
    if (isEmptyString(downloadUrl) || isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      await downloadFileFromUrl(downloadUrl, media.fileName ?? 'document.pdf');
    } catch {
      notify({
        variant: 'error',
        message: 'Failed to download file',
      });
    } finally {
      setIsDownloading(false);
    }
  }, [downloadUrl, isDownloading, media.fileName, notify]);

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
      <span className="text-sm font-medium text-gray-900 truncate min-w-0 flex-1">
        {media.fileName ?? 'Document'}
      </span>
      <div className="flex items-center justify-end shrink-0">
        <IconButton
          tooltip="Preview"
          size="small"
          onClick={() => { onPreview(media); }}
          className="!p-1.5"
        >
          <VisibilityIcon className="!w-4 !h-4 text-gray-600" />
        </IconButton>
        <IconButton
          tooltip="Print"
          size="small"
          disabled={isRowPrinting || mediaId <= 0}
          onClick={() => { onPrint(mediaId); }}
          className="!p-1.5"
        >
          {isRowPrinting ? (
            <Spinner size={16} />
          ) : (
            <PrinterIcon className="!w-4 !h-4 text-gray-600" />
          )}
        </IconButton>
        <IconButton
          tooltip="Download"
          size="small"
          disabled={!downloadUrl || isDownloading}
          onClick={handleDownload}
          className="!p-1.5"
        >
          {isDownloading ? (
            <Spinner size={16} />
          ) : (
            <DownloadIcon className="!w-4 !h-4 text-gray-600" />
          )}
        </IconButton>
      </div>
    </div>
  );
}

export default function VisitNoteFormPreviewDialog({
  patient: patientFromParams,
  patientMultiMediaIds,
  includeVisitNote,
  category,
  onClose,
}: Props) {
  const defaultPatient = useDefaultPatient();
  const patient = patientFromParams ?? defaultPatient;
  const notify = useNotify();
  const userInfo = useUserInfo();
  const onPreview = usePreviewMediaClick();
  const [printingMediaId, setPrintingMediaId] = useState<number | null>(null);
  const { print, isPrinting } = usePrintMediaRequest<[number]>(
    getApiMultimediaGetPatientMultiMediaByIdPatientMultiMediaId,
  );
  const {
    mediaList,
    isLoading,
  } = useResolvePatientMultimedia({
    mediaIds: patientMultiMediaIds,
  });

  useEffect(() => {
    if (!isPrinting) {
      setPrintingMediaId(null);
    }
  }, [isPrinting]);

  const handlePrint = useCallback((mediaId: number) => {
    setPrintingMediaId(mediaId);
    print([mediaId]);
  }, [print]);

  const handleSendFax = useCallback(() => {
    if (isNil(patient)) {
      notify({
        variant: 'error',
        message: 'Patient is required to send a fax.',
      });
      return;
    }

    onClose();
    faxEventChannel.emit('onOpenSendFax', {
      patient: patient as PatientCompact,
      patientMultimediaIds: patientMultiMediaIds,
      attachFaceSheet: false,
      shouldCreateFollowUpTodo: isOrderOrReferralCategory(category),
    });
  }, [notify, onClose, patient, patientMultiMediaIds, category]);

  const visitNoteMessage = includeVisitNote
    ? 'Visit note included with these documents.'
    : 'Visit note has not been included.';
  const documentCount = mediaList.length;
  const savedMessage = documentCount > 1
    ? `${documentCount} documents have been saved to the patient's chart.`
    : `${category.name} has been saved to the patient's chart.`;

  return (
    <Dialog
      open
      title={`Print or Fax ${category.name}`}
      onClose={onClose}
      maxWidth={false}
      className="populate-form !w-full !min-w-[28rem] !max-w-[40rem]"
    >
      <div className="flex flex-col gap-4 !mt-2 !space-y-0">
        <div className="flex gap-2 items-start">
          <CheckCircleIcon className="!w-5 !h-5 !text-populate-green-1" />
          <div className="flex flex-col gap-1 items-start">
            <span className="text-sm font-semibold">{savedMessage}</span>
            <span className="text-xs text-gray-500 font-medium">{visitNoteMessage}</span>
          </div>
        </div>
        <div
          className={clsx(
            'overflow-hidden rounded-lg border border-gray-200 bg-white',
            { 'min-h-[8rem] flex items-center justify-center': isLoading },
          )}
        >
          {isLoading && <Spinner />}
          {!isLoading && mediaList.length === 0 && (
            <p className="p-4 text-sm text-gray-500">No documents available.</p>
          )}
          {!isLoading && mediaList.length > 0 && (
            <div className="max-h-[27rem] overflow-y-auto">
              {mediaList.map((media) => (
                <FormWorkflowDocumentRow
                  key={media.mediaId}
                  media={media}
                  onPrint={handlePrint}
                  onPreview={onPreview}
                  printingMediaId={printingMediaId}
                  isPrinting={isPrinting}
                />
              ))}
            </div>
          )}
        </div>
        <div className="-mx-5 px-5 flex justify-between items-center gap-3 pt-6">
          <Button
            variant="outlined"
            type="button"
            onClick={onClose}
          >
            Close
          </Button>
          {!isNil(userInfo?.organization?.eFaxConfig) && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendFax}
              type="button"
              disabled={patientMultiMediaIds.length === 0}
              className="flex gap-2 items-center !h-8 !py-0 !px-3"
            >
              <FaxIcon className="!w-4 !h-4" />
              Send Fax
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
